import React, { useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, RotateCw, Search, Store } from "lucide-react";
import { useSelector } from "react-redux";
import { WebSocketContext } from "../../../App";
import wsSend_request from "../../../Api/ws/ws_request";
import { franchiseReplyOk, pickStores } from "./storeRules";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

// The socket answers into Redux and has no error callback, so a reply that
// never comes is treated as a failure after this long.
export const PICKER_TIMEOUT_MS = 15000;

// Find any store, in D2C or not, to open its readiness and switch. Uses the
// existing `franchise_list` socket call (em-biz-backend app/globals.py
// VAR_URLS['franchise_list']); limit 5000 fills AllDealerFranchiseTypeListAll,
// the same list other screens use for their dealer pickers.
const StorePicker = ({ show, onHide, onPick }) => {
  const socket = useContext(WebSocketContext);
  const websocket = socket?.websocket;
  const { device_id, AllDealerFranchiseTypeListAll } = useSelector((state) => state.adminReducers);
  const franchises = AllDealerFranchiseTypeListAll?.list;
  const [search, setSearch] = useState("");
  // idle -> loading -> ready | failed (with why: "no-reply" or "bad-reply")
  const [status, setStatus] = useState("idle");
  const [failure, setFailure] = useState("");
  // The stored reply object when this picker asked. The reducer builds a new
  // object for every franchise_list reply, so a different object means a reply
  // arrived after our request.
  const sentWith = useRef(null);

  const fail = (why) => {
    setFailure(why);
    setStatus("failed");
  };

  const request = () => {
    if (!websocket) {
      fail("no-reply");
      return;
    }
    sentWith.current = AllDealerFranchiseTypeListAll;
    setFailure("");
    setStatus("loading");
    wsSend_request(websocket, {
      transmit: "broadcast",
      url: "franchise_list",
      request: { status: true, limit: 5000, page_no: 1, search: "" },
      DeviceId: device_id,
    });
  };

  const requestRef = useRef(request);
  requestRef.current = request;

  // First open: use a list another screen already loaded, or ask for one.
  useEffect(() => {
    if (!show || status !== "idle") return;
    if (franchiseReplyOk(AllDealerFranchiseTypeListAll)) setStatus("ready");
    else requestRef.current();
  }, [show, status, AllDealerFranchiseTypeListAll]);

  // Only a reply that arrived after our request counts, and only a well-formed
  // list is a success; an error-shaped reply is a failure.
  useEffect(() => {
    if (status !== "loading" || AllDealerFranchiseTypeListAll === sentWith.current) return;
    if (franchiseReplyOk(AllDealerFranchiseTypeListAll)) setStatus("ready");
    else fail("bad-reply");
  }, [AllDealerFranchiseTypeListAll, status]);

  useEffect(() => {
    if (status !== "loading") return undefined;
    const timer = setTimeout(() => {
      setFailure("no-reply");
      setStatus((current) => (current === "loading" ? "failed" : current));
    }, PICKER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const matches = status === "ready" ? pickStores(franchises, search) : [];

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="tw-max-w-xl">
        <DialogHeader>
          <DialogTitle>Find a store</DialogTitle>
          <DialogDescription>
            Search by store name or franchise code. Opening a store shows whether it is ready for D2C before you switch
            it on.
          </DialogDescription>
        </DialogHeader>
        <div className="tw-relative">
          <Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-size-4 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Store name or code"
            aria-label="Search stores"
            className="tw-h-10 tw-pl-9"
            autoFocus
          />
        </div>
        {status === "loading" && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-muted-foreground" role="status">
            <Loader2 className="tw-size-4 tw-animate-spin" aria-hidden="true" /> Loading stores...
          </div>
        )}
        {status === "failed" && (
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-800"
            role="alert"
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <AlertTriangle className="tw-size-4 tw-shrink-0" aria-hidden="true" />
              {failure === "bad-reply"
                ? "Could not load the store list: the server answered with an error or no stores. Try again."
                : "Could not load the store list. Check your connection and try again."}
            </span>
            <Button size="sm" variant="outline" onClick={request}>
              <RotateCw aria-hidden="true" /> Retry
            </Button>
          </div>
        )}
        {status === "ready" && search.trim() && !matches.length && (
          <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">No store matches.</div>
        )}
        {status === "ready" && !search.trim() && (
          <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">Type a name or code to search.</div>
        )}
        {matches.length > 0 && (
          <ul className="tw-m-0 tw-max-h-80 tw-list-none tw-divide-y tw-overflow-y-auto tw-rounded-md tw-border tw-p-0">
            {matches.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onPick(item.id)}
                  className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-font-sans tw-text-sm tw-text-foreground hover:tw-bg-accent focus-visible:tw-bg-accent focus-visible:tw-outline-none"
                >
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <Store className="tw-size-4 tw-text-muted-foreground" aria-hidden="true" />
                    {item.customer_name || "Unnamed store"}
                  </span>
                  <span className="tw-font-mono tw-text-xs tw-text-muted-foreground">{item.franchise_code || ""}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StorePicker;
